import { useEffect, useState } from 'react';
import { pagesOpenedBy } from '@obraplan/core/domain/access/pages';
import type { Role } from '@obraplan/core/domain/access/role';
import type { Project } from '@obraplan/core/domain/structure/project';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Field } from '@/components/ui/input';

/**
 * Atribuir papel — **com o alcance escolhido na hora** (RN-133).
 *
 * O modelo sempre soube guardar papel por obra; a tela é que só sabia conceder papel
 * global, e todo perfil acabava enxergando todas as obras. Aqui o alcance é uma escolha
 * explícita, e é a primeira coisa que se lê depois do papel: quem concede precisa
 * decidir *onde* aquilo vale, não descobrir depois.
 *
 * Um papel por obra **não** administra o sistema: permissões de perfil, backup e
 * catálogos globais só saem de papel global — quem tem `ADMIN` numa obra não ganha o
 * sistema inteiro junto.
 */
export interface AssignRoleSubmit {
  readonly roleId: string;
  /** Ausente = todas as obras. */
  readonly projectId?: string;
}

export interface AssignRoleDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  userName: string;
  roles: readonly Role[];
  projects: readonly Project[];
  isPending: boolean;
  errorMessage?: string;
  onSubmit: (input: AssignRoleSubmit) => void;
}

const ALL_PROJECTS = 'ALL';

export function AssignRoleDialog({
  open,
  onOpenChange,
  userName,
  roles,
  projects,
  isPending,
  errorMessage,
  onSubmit,
}: AssignRoleDialogProps) {
  const [roleId, setRoleId] = useState('');
  const [scope, setScope] = useState<string>(ALL_PROJECTS);
  // Quem concede precisa ver o que está concedendo: o nome do papel não diz que telas abre.
  const selected = roles.find((role) => role.id === roleId);

  useEffect(() => {
    if (!open) return;
    setRoleId(roles[0]?.id ?? '');
    // O alcance começa na obra, e não em "todas": conceder amplo demais é o erro caro,
    // e o padrão de um formulário é o que a maioria vai aceitar sem pensar.
    setScope(projects[0]?.id ?? ALL_PROJECTS);
  }, [open, roles, projects]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Atribuir papel a {userName}</DialogTitle>
          <DialogDescription>
            O papel vale em todas as obras ou apenas na obra escolhida. Papel de uma obra não
            alcança as outras nem administra o sistema.
          </DialogDescription>
        </DialogHeader>

        <div className="flex flex-col gap-4">
          <Field
            label="Papel"
            htmlFor="assign-role"
            required
            {...(selected === undefined
              ? {}
              : {
                  hint: `Abre: ${
                    pagesOpenedBy(new Set(selected.permissions))
                      .map((page) => page.label)
                      .join(', ') || 'nenhuma tela'
                  }.`,
                })}
          >
            <select
              id="assign-role"
              className="h-11 w-full rounded-md border border-(--color-border-subtle) bg-(--color-surface) md:h-9 px-2 text-sm"
              value={roleId}
              onChange={(event) => setRoleId(event.target.value)}
            >
              {roles.length === 0 && <option value="">Nenhum papel disponível</option>}
              {roles.map((role) => (
                <option key={role.id} value={role.id}>
                  {role.name}
                </option>
              ))}
            </select>
          </Field>

          <Field
            label="Alcance"
            htmlFor="assign-scope"
            required
            hint="Todas as obras dá acesso também às obras criadas daqui em diante."
            {...(errorMessage === undefined ? {} : { error: errorMessage })}
          >
            <select
              id="assign-scope"
              className="h-11 w-full rounded-md border border-(--color-border-subtle) bg-(--color-surface) md:h-9 px-2 text-sm"
              value={scope}
              onChange={(event) => setScope(event.target.value)}
            >
              {projects.map((project) => (
                <option key={project.id} value={project.id}>
                  {project.name}
                </option>
              ))}
              <option value={ALL_PROJECTS}>Todas as obras</option>
            </select>
          </Field>
        </div>

        <DialogFooter>
          <Button variant="secondary" onClick={() => onOpenChange(false)}>
            Cancelar
          </Button>
          <Button
            variant="primary"
            disabled={isPending || roleId === ''}
            onClick={() =>
              onSubmit({
                roleId,
                ...(scope === ALL_PROJECTS ? {} : { projectId: scope }),
              })
            }
          >
            {isPending ? 'Atribuindo…' : 'Atribuir'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
